/**
 * Production probe for refused browser-storage writes.
 *
 * `safe-storage` keeps a refusal from breaking the app, but a swallowed failure is also an
 * unobserved one: production reported `QuotaExceededError: The quota has been exceeded.` from the
 * NgRx persistence metareducer, and the only evidence available afterwards was a dump of what was
 * stored -- which is exactly the evidence that cannot explain it. A refused `setItem` writes
 * NOTHING, so the surviving contents look innocent whatever the cause, and the value that was
 * actually refused is gone. This module captures that value's SHAPE at the moment it is refused.
 *
 * It answers the two questions a post-mortem dump cannot:
 *
 * 1. How big was the write, and which slice of the store made it that big (`chars`, `slices`,
 *    `dynaData`, `top`)? The metareducer serializes the WHOLE store on every non-router action, so
 *    one feature parking bulk rows in state is enough to push every later write over the edge.
 * 2. Was the browser refusing that size, or refusing everything (`ceiling`, `storage`)? A Safari
 *    private window, "block all cookies", disk pressure or evicted site data all refuse writes with
 *    storage nearly empty -- in which case the payload size is a red herring and no amount of
 *    trimming state would have helped.
 *
 * NOTHING STORED HERE IS USER DATA. Every field is a length, a count, a key name, an action type or
 * a URL path -- never a stored value, a row, or a query string. This ships to production and is
 * posted to the backend; it has to be safe to read in a bug report.
 */

import { environment } from 'src/environments/environment';
import {
  StorageFailure, inventoryStorage, probeWriteCeiling, readStorageJson, writeStorageJson,
} from './safe-storage';

/**
 * localStorage key holding the local copy of the ring (a fallback for when the POST cannot go).
 *
 * Exported because it has to survive the wipes the app does on its own storage: it is the only
 * record left when the report never reaches the backend, and it carries no user data, so anything
 * clearing localStorage wholesale must carry it over (see `AppComponent.ngOnDestroy`).
 */
export const QUOTA_DIAG_STORAGE_KEY = 'quota-diag';
const DIAG_KEY = QUOTA_DIAG_STORAGE_KEY;

/**
 * How many failures the local ring keeps. Deliberately tiny: the ring lives in the storage that is
 * currently refusing writes, so it must be able to fit in whatever room is left, and consecutive
 * failures in one page are near-duplicates anyway (the payload grows, the cause does not change).
 */
const DIAG_MAX = 3;

/** How many entries the slice and dynaData breakdowns keep. Enough to see the shape, not a dump. */
const MAX_BREAKDOWN = 12;

/** Per-slot detail for the biggest `dynaData` entry -- the usual suspect behind a fat blob. */
export interface QuotaDiagTopSlot {
  /** The route code keying the slot (a feature name like `reports`, never user data). */
  code: string;
  chars: number;
  /** Size of the slot's loaded result set, and how many rows it holds. */
  dataChars: number;
  dataRows: number | null;
  /** Size of the slot's selection, and how many rows are selected. */
  selectionChars: number;
  selectionRows: number | null;
  /** Size of the slot's search request (filters/sort/pagination). */
  requestChars: number;
}

/** One refused write, reduced to sizes and counts. */
export interface QuotaDiag {
  /** ISO timestamp of the refusal. */
  at: string;
  area: string;
  op: string;
  key: string | null;
  error: { name: string; message: string };
  /** Length of the string the browser refused. `null` when the failure carried no payload. */
  chars: number | null;
  /** Top-level store slices by size, biggest first. */
  slices: Array<[string, number]>;
  /** `conf.dynaData` slots by size, biggest first -- one entry per feature that has list state. */
  dynaData: Array<[string, number]>;
  /** Breakdown of the biggest `dynaData` slot. */
  top: QuotaDiagTopSlot | null;
  /** What is in storage right now, and how much room it takes. */
  storage: { usedChars: number; keys: Array<[string, number]> };
  /**
   * Largest single value storage accepts right now, or `null` when it accepts nothing. Probed no
   * higher than `chars` needed, so it is a bound to compare against `chars` -- not an absolute max.
   *
   * ALWAYS read `ceilingProbed` first: `null` means "accepts nothing" only when the probe ran.
   */
  ceiling: number | null;
  /**
   * Whether the ceiling probe ran at all. `false` on a `serialize` failure -- the browser was never
   * asked to store anything, so there is nothing to measure and `ceiling` carries no information.
   */
  ceilingProbed: boolean;
  /**
   * NgRx action type whose dispatch triggered the write, and `null` for a failure that did not come
   * from the persistence metareducer -- a diagnostic ring, a preference, a draft key. Attributed by
   * matching the failing key against the one the metareducer stamped, so an unrelated failure is
   * never labelled with whatever action happened to be persisted last.
   */
  action: string | null;
  /** Route PATH only -- never the query string, which carries filter values. */
  path: string;
  appVersion: string;
  userAgent: string;
}

/**
 * The action type currently being persisted, stamped by the metareducer just before it writes,
 * together with the key it is about to write to.
 *
 * A module-level slot rather than a parameter because the failure surfaces two layers down, inside
 * `safe-storage`, which has no business knowing what NgRx is. It is the single most useful field
 * for reproducing the failure -- it names the interaction that triggered it.
 *
 * The KEY is stamped alongside it so the action is only ever attached to the failure it actually
 * describes: every other storage user in the app (the diagnostic rings, preferences, draft slots)
 * also fails through the same sink, and labelling one of those with the last persisted action would
 * point an investigation at an interaction that had nothing to do with it.
 */
let lastPersistAction: string | null = null;
let lastPersistKey: string | null = null;

/** Stamp the action about to be persisted, and the key it targets. Called by the metareducer only. */
export function setLastPersistAction(actionType: string | null, key: string | null = null): void {
  lastPersistAction = actionType;
  lastPersistKey = key;
}

/**
 * The stamped action, but only for a failure on the key it was stamped for.
 *
 * Exported because the record is built OFF the dispatch chain (see `QuotaDiagReporterService`): by
 * then the metareducer has stamped later actions, so the consumer reads this the moment the failure
 * arrives and hands the result to `buildQuotaDiag`.
 */
export function getLastPersistAction(key: string | null): string | null {
  return key !== null && key === lastPersistKey ? lastPersistAction : null;
}

/** Serialized size of a value, or 0 when it cannot be measured. Never throws. */
function charsOf(value: unknown): number {
  try {
    return value === undefined ? 0 : (JSON.stringify(value) ?? '').length;
  } catch {
    return 0;
  }
}

/** `[key, size]` pairs for an object's own entries, biggest first, capped. */
function breakdown(value: unknown): Array<[string, number]> {
  if (value === null || typeof value !== 'object') {
    return [];
  }
  return Object.entries(value as Record<string, unknown>)
    .map(([key, entry]) => [key, charsOf(entry)] as [string, number])
    .sort((a, b) => b[1] - a[1])
    .slice(0, MAX_BREAKDOWN);
}

/** Row count of an array-ish value, or `null` when it is not a list. */
function rowsOf(value: unknown): number | null {
  return Array.isArray(value) ? value.length : null;
}

/**
 * Detail for the biggest `dynaData` slot: which feature it belongs to, and whether its bulk is the
 * loaded result set or the selection. Both are things that do not belong in persisted state, and
 * telling them apart points at a different fix.
 *
 * Takes the already-computed breakdown rather than building its own: every entry in it costs a full
 * `JSON.stringify` of a slot, and this whole function runs synchronously inside the reducer that
 * just failed to persist a blob big enough to be refused.
 */
function topSlotOf(dynaData: unknown, sizes: Array<[string, number]>): QuotaDiagTopSlot | null {
  if (sizes.length === 0) {
    return null;
  }
  const [code, chars] = sizes[0];
  const slot = (dynaData as Record<string, any>)[code] ?? {};
  const selection = slot?.['routeData']?.['selection'];
  return {
    code,
    chars,
    dataChars: charsOf(slot?.['data']),
    dataRows: rowsOf(slot?.['data']) ?? rowsOf(slot?.['data']?.['content']),
    selectionChars: charsOf(selection),
    selectionRows: rowsOf(selection?.['rows']),
    requestChars: charsOf(slot?.['request']),
  };
}

/**
 * Reduce a storage failure to sizes and counts.
 *
 * Every step is individually guarded: a half-built diagnostic still names the failing key, the
 * error and the payload size, and that is already more than production has today. The failure this
 * runs on has itself only just been swallowed -- throwing from here would undo the guarantee that
 * `safe-storage` exists to provide.
 *
 * <p>This is the expensive half of the probe: `breakdown` costs a `JSON.stringify` per slice, and
 * the ceiling probe writes into the area. The caller runs it OFF the NgRx dispatch chain for that
 * reason, and passes the `action` it read when the failure arrived (by then the metareducer has
 * stamped later ones over it).
 */
export function buildQuotaDiag(failure: StorageFailure, action?: string | null): QuotaDiag {
  const error = failure.error as { name?: string; message?: string } | null;
  const diag: QuotaDiag = {
    at: new Date().toISOString(),
    area: failure.area,
    op: failure.op,
    key: failure.key,
    error: { name: error?.name ?? 'Error', message: error?.message ?? String(failure.error) },
    chars: failure.raw === null ? null : failure.raw.length,
    slices: [],
    dynaData: [],
    top: null,
    storage: { usedChars: 0, keys: [] },
    ceiling: null,
    ceilingProbed: false,
    // The caller passes the action it read when the failure ARRIVED: this function runs off the
    // dispatch chain, by which point the metareducer has stamped later actions over it.
    action: action !== undefined ? action : getLastPersistAction(failure.key),
    // Pathname only. The query string carries filter values, i.e. user data.
    path: typeof location === 'undefined' ? '' : location.pathname,
    appVersion: environment.appVersion,
    userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
  };

  try {
    const source = failure.source;
    if (source !== null && typeof source === 'object') {
      diag.slices = breakdown(source);
      const dynaData = (source as Record<string, any>)['conf']?.['dynaData'];
      diag.dynaData = breakdown(dynaData);
      diag.top = topSlotOf(dynaData, diag.dynaData);
    }
  } catch {
    // Shape analysis is the optional half; the sizes above are the half that always lands.
  }

  try {
    const keys = inventoryStorage(failure.area === 'session' ? 'session' : 'local');
    diag.storage = {
      usedChars: keys.reduce((total, [key, size]) => total + key.length + size, 0),
      keys: keys.slice(0, MAX_BREAKDOWN),
    };
  } catch {
    // Enumeration can be refused in exactly the modes that caused the failure being reported.
  }

  // A `serialize` failure never reached the browser -- `JSON.stringify` threw, or returned nothing
  // storable. Measuring what storage would have accepted answers a question that was never asked,
  // and it is not free: the probe writes filler into the area and, on a healthy one, the top rung
  // SUCCEEDS. `ceilingProbed` stays false so `ceiling: null` is not misread as "accepts nothing".
  if (failure.op !== 'serialize') {
    try {
      // Scaled to the refused payload: probing above it would allocate megabytes and push an area
      // already under pressure, for an answer the report does not use (see probeWriteCeiling).
      diag.ceiling = probeWriteCeiling(failure.area === 'session' ? 'session' : 'local', diag.chars);
      diag.ceilingProbed = true;
    } catch {
      // The probe is already internally guarded; this is belt and braces on the failure path.
    }
  }

  return diag;
}

/**
 * Append to the local ring.
 *
 * Best-effort on purpose: this writes to the storage that just refused a write, so it will often
 * fail, and `safe-storage` swallows that too (its re-entrancy latch keeps this from re-triggering
 * the sink that called us). The backend POST is the primary channel; this is what is left when the
 * user is offline, logged out, or the request never lands.
 */
export function appendQuotaDiag(diag: QuotaDiag): void {
  const previous = readStorageJson<QuotaDiag[]>('local', DIAG_KEY, []);
  const ring = (Array.isArray(previous) ? previous : []).concat(diag);
  writeStorageJson('local', DIAG_KEY, ring.slice(-DIAG_MAX));
}

/**
 * Console hooks for pulling the ring off a user's machine: `copy(__quotaDiag())` in the browser
 * console yields the JSON to paste into a bug report, and `__clearQuotaDiag()` resets it.
 *
 * Registered unconditionally (including in production) because the whole point is to be reachable
 * on the machine where the failure actually happens -- which is never a developer's.
 */
if (typeof window !== 'undefined') {
  (window as any)['__quotaDiag'] = () =>
    JSON.stringify(readStorageJson<QuotaDiag[]>('local', DIAG_KEY, []), null, 2);
  (window as any)['__clearQuotaDiag'] = () => writeStorageJson('local', DIAG_KEY, []);
}
